import type { VoidInspectionContext } from '~/components/Dialogs/void-inspection-screen.vue'
import type { UserBioContext } from '~/components/My-Cover/staff-properties/bio.vue'
import usePurchaseApi from '~/services/apis/purchases-info'
import type { PaymentResponse } from '~/components/Gateway/channel-modal.vue'
import useStaffActivation from '~/composables/useStaffActivation'
import { useResourcesApi } from '~/services/apis/resources'
import useClaimsApi from '~/services/apis/useClaimsApi'
import type { IdAndName } from '@/types'

export default function usePolicyInfo(
	payload: MaybeRefOrGetter<{
		custodian_id?: string
		policyInfo: PolicyResponse | null
		pending?: boolean
		checkForClaim?: boolean
		refresh?: Function
	}>
) {
	const _payload = toRef(payload)
	const { back, replace } = useRouter()
	const { urlQuery, productCategoryType, addProductsIconFill, getStatus, baseUrl, toLower } = useUtils()
	const { openStaffRecord, notSupported, activateModal } = useStaffActivation(ref(null), {
		purchase_id: _payload.value.policyInfo?.purchase_id
	})
	const { checkPolicyForClaim } = useClaimsApi()
	const { downloadFile } = useFileExplorer()
	const { initializeSdk } = useMcaSdk()
	const { addNewDependantSlot } = usePurchaseApi()
	const terminateDialog = shallowRef(false)
	const downloadingDocs = shallowRef(false)
	const canMakeClaim = shallowRef(false)
	const verifyingPolicy = shallowRef(false)
	const summaryContent = ref()
	const formRecord = ref()
	const { formatDate } = useCustomDate()
	const { screenSizesBreak } = useMobile()
	const mixpanel = useMixpanel()
	const loaderProps = reactive({
		active: false,
		text: ''
	})
	const dialogs = ref({
		changeCustodian: false,
		isClaimsModal: false,
		activationModal: false,
		custodianModal: false,
		isInspectionNotSupported: false,
		confirmClaim: false,
		newSlot: false,
		isPaymentOpen: false
	})
	const toMakePayment = computed(() => Boolean(urlQuery.value?.page === 'make-payment'))
	const inspectionData = shallowRef<VoidInspectionContext>({
		title: '',
		subTitle: '',
		QrCodeLink: ''
	})

	const { refresh } = _payload.value
	const policy = computed(() => _payload.value.policyInfo || null)
	const productName = computed(() => policy.value?.product?.name ?? '')
	const productCategory = computed(() => toLower(policy.value?.product?.productCategory?.name ?? ''))
	const custodianId = computed(() => _payload.value.custodian_id ?? '')
	const provider = computed(() => policy.value?.product?.underwriter ?? '')

	const categoryType = computed(() => productCategoryType(productCategory.value, productName.value))
	const fromEmployeesRoute = computed(() => {
		// Check if the previous route was from employees to render
		return window.history.state?.back?.includes('employees') || false
	})
	const healthPolicyType = computed(() => policy.value?.purchase?.meta?.health_plan_type ?? '')
	const isPolicyChurned = computed(() => String(policy.value?.wait_untill ?? ''))
	const isExpired = computed(() => policy.value?.is_expired ?? false)

	const currentPage = computed(() => {
		return urlQuery.value?.page as '' | 'summary' | 'need_to_know' | 'activation-done'
	})
	const planStatus = computed(() => {
		return getStatus(policy.value)
	})
	const canBeDeactivated = computed(() => {
		return (
			!planStatus.value?.to_be_removed && !planStatus.value?.to_be_terminated && !planStatus.value?.is_deactivated
		)
	})
	const expiryDate = computed(() =>
		Number(policy.value?.meta?.month ?? 0) > 1
			? (policy.value?.actual_renewal_date ?? policy.value?.policy_expiration_date ?? '')
			: (policy.value?.policy_expiration_date ?? '')
	)
	const purchaseDate = computed(() => policy.value?.purchase?.purchase_date ?? policy.value?.purchase_date ?? '')
	const dateAdded = computed(() => policy.value?.created_at ?? '')
	const activePolicy = computed(() => {
		if (urlQuery.value?.status === 'pending') {
			return null
		}
		return policy.value
	})
	const isPolicyClaimable = computed(() => activePolicy.value?.product?.claimable)
	const isPolicyPending = computed(() => {
		return policy.value?.is_pending || false
	})
	const activationFailed = computed(() => policy.value?.activation_failed ?? false)
	const isAssigned = computed(() => policy.value?.email !== null && policy.value?.email !== undefined)
	const isPolicyPaused = computed(() => {
		return policy.value?.meta?.is_paused || false
	})
	const staffName = computed(
		() =>
			`${policy.value?.meta?.first_name || policy.value?.first_name || 'N/A'} ${
				policy.value?.meta?.last_name || policy.value?.last_name || 'N/A'
			}`
	)
	const isDeactivated = computed(() => policy.value?.is_deactivated ?? false)
	const isToBeRemoved = computed(() => policy.value?.to_be_removed ?? false)
	const hasInspected = computed(() => {
		if (categoryType.value?.isThirdParty) return true
		if (categoryType.value?.isAuto) {
			return Boolean(policy.value?.meta?.vehicleInspectionMeta?.timestamp ?? false)
		}
		if (categoryType.value?.isGadget) {
			return Boolean(policy.value?.meta?.gadgetInspectionMeta?.timestamp ?? false)
		}
		return false
	})

	const pendingDependantSlots = computed(() =>
		// @ts-ignore
		(policy.value?.dependantPendingPolicies ?? []).map(policy => ({ ...policy, is_pending: true }))
	)
	const hasPendingSlots = computed(() => pendingDependantSlots.value.length > 0)
	const canBuySlot = computed(() => !hasPendingSlots.value)

	const isStaging = Boolean(import.meta.env.VITE_DEV === 'true')
	const planIsGreaterThan5Days = computed(() => {
		return isAssigned.value && isOver5Days(policy.value?.created_at ?? '')
	})
	const planIsGreaterThan24Hours = computed(() => {
		return isAssigned.value && isOver24Hours(policy.value?.created_at ?? '')
	})

	const dependantType = computed(() => policy.value?.meta?.dependant_type ?? '')
	const policyNumber = computed(() => {
		if (categoryType.value.isHealth) {
			return planIsGreaterThan24Hours.value ? policy.value?.policy_number : '---'
		}
		return policy.value?.policy_number || '---'
	})
	const policyCertificate = computed(() => policy.value?.policy_certificate_url ?? '')
	const profileBio = computed<UserBioContext>(() => {
		return {
			'Staff Full Name': {
				value: staffName.value,
				disabled: !categoryType.value?.isTravel
			},
			'Gadget policy holder': {
				value: staffName.value,
				disabled: !categoryType.value?.isGadget
			},
			'Vehicle policy holder': {
				value: staffName.value,
				disabled: !categoryType.value?.isAuto
			},
			'email address': {
				value: policy.value?.meta?.email || policy.value?.email || '---'
			},
			'phone number': {
				value: policy.value?.meta?.phone_number || policy.value?.phone_number || '---'
			},
			gender: {
				value: policy.value?.meta?.gender || policy.value?.gender || '---'
			},
			'date of birth': {
				value:
					(policy.value?.meta?.date_of_birth || policy.value?.date_of_birth) &&
					formatDate(policy.value?.date_of_birth ?? policy.value?.meta?.date_of_birth ?? ''),
				disabled: categoryType.value?.isTravel
			},
			'Policy Holder Valid I.D': {
				value: policy.value?.meta?.valid_driver_id || policy.value?.meta?.valid_id,
				disabled: !categoryType.value?.isAuto && !categoryType.value?.isGadget
			}
		}
	})
	const planName = computed(() => {
		return policy.value?.product.name?.toLowerCase() || ''
	})
	const categoryName = computed(() => {
		return toLower(policy.value?.product.productCategory.name || '')
	})
	const pageTitle = computed(() => {
		const summaryTitle = (currentPage.value === 'summary' || toMakePayment.value) && 'Plan Summary'
		const summarySubTitle =
			(currentPage.value === 'summary' || toMakePayment.value) &&
			'Confirm details below and proceed to make payment'
		const needToKnowTitle = currentPage.value === 'need_to_know' && 'Need To Know'
		const needToKnowSubTitle =
			currentPage.value === 'need_to_know' && 'Important things to note about the next steps'
		const policyName =
			categoryType.value.isHealth || categoryType.value?.isTravel
				? 'Staff'
				: categoryType.value?.isAuto
					? 'Vehicle'
					: categoryType.value?.isGadget
						? 'Gadget'
						: ''
		return {
			title: summaryTitle || needToKnowTitle || `${policyName} Details`,
			subTitle:
				summarySubTitle || needToKnowSubTitle || `View and manage ${policyName?.toLowerCase()} information`
		}
	})
	const productTypeName = computed(() => {
		let name = ''
		const planDetail = policy.value
		const metaInfo = planDetail?.meta
		if (categoryType.value.isHealth) {
			name = staffName.value
		} else if (categoryType.value?.isTravel) {
			name = renderItem(metaInfo?.location_name || metaInfo?.destination)
		} else if (categoryType.value?.isAuto) {
			name = renderItem((metaInfo?.vehicle_make as IdAndName)?.name || (metaInfo?.vehicle_make as string))
		} else if (categoryType.value?.isGadget) {
			name = renderItem((metaInfo?.gadget_brand as IdAndName)?.name || (metaInfo?.gadget_brand as string))
		}

		return {
			name
		}
	})
	const tabCollection = computed<lineTabContext[]>(() => [
		{
			key: 'staff_profile',
			name: 'Staff Details',
			disabled: screenSizesBreak.biggerThanMd
		},
		{
			key: 'gadget_info',
			name: 'Gadget Details',
			disabled: !categoryType.value?.isGadget
		},
		{
			key: 'vehicle_info',
			name: 'Vehicle Details',
			disabled: !categoryType.value?.isAuto
		},
		{
			key: 'travel_info',
			name: 'Travel Details',
			disabled: !categoryType.value?.isTravel
		},
		{
			key: 'travel_document',
			name: 'Document',
			disabled: !categoryType.value?.isTravel
		},
		{
			key: 'next_of_kin',
			name: 'Next of kin',
			disabled: !categoryType.value?.isTravel
		},
		{
			key: 'utilization',
			name: 'Claims',
			disabled: categoryType.value.isHealth || _payload.value.pending
		},
		{
			key: 'inspection',
			name: 'Inspection',
			disabled: (!categoryType.value?.isAuto && !categoryType.value?.isGadget) || categoryType.value?.isThirdParty
		},

		{
			key: 'health_info',
			name: 'Policy Details',
			disabled: !categoryType.value.isHealth,
			active: categoryType.value.isHealth
		},
		{
			key: 'dependant',
			name: 'Dependant',
			disabled: !categoryType.value.isHealth,
			active: false
		},
		{
			key: 'activities',
			name: 'Activities',
			disabled: !categoryType.value.isHealth
		},
		{
			key: 'amount_used',
			name: 'Amount Used',
			disabled: !categoryType.value.isHealth
		}
	])
	const activeTab = computed(() => String(tabCollection.value.find(item => !item.disabled)?.key ?? ''))
	const renewalDate = computed(() => {
		const dates = {
			raw: '',
			formatted: ''
		}
		if (categoryType.value.isHealth) {
			dates.raw = policy.value?.meta?.actual_renewal_date ?? ''
			dates.formatted = formatDate(dates.raw)
		} else {
			dates.raw = policy.value?.policy_expiration_date ?? ''
			dates.formatted = formatDate(dates.raw)
		}
		return dates
	})
	const activationDate = computed(() => {
		return {
			raw: policy.value?.activation_date ?? '',
			formatted: formatDate(policy.value?.activation_date ?? '')
		}
	})
	const isFamilyPlan = computed(() => toLower(policy.value?.purchase?.meta?.health_plan_type) === 'family')

	const totalNoOfDependant = computed(() => policy.value?.no_of_dependant ?? policy.value?.meta?.no_of_dependant ?? 0)
	const isInspectable = computed(() => policy.value?.product?.inspectable ?? false)
	const vehicleMeta = computed(() => policy.value?.meta?.vehicleInspectionMeta ?? null)
	// const gadgetMeta = computed(() => policy.value?.meta?.gadgetInspectionMeta ?? null)
	const coverPeriod = computed(() => policy.value?.meta?.cover_period ?? '')
	const policyCustomerDetails = computed(() => ({
		first_name: policy.value?.meta?.first_name ?? policy.value?.first_name ?? '',
		last_name: policy.value?.meta?.last_name ?? policy.value?.last_name ?? '',
		email: policy.value?.meta?.email ?? policy.value?.email ?? '',
		phone: policy.value?.meta?.phone_number ?? policy.value?.phone_number ?? '',
		address: policy.value?.meta?.address ?? policy.value?.address ?? ''
	}))
	const metaInfo = computed(() => ({
		plate_number: policy.value?.meta.plate_number ?? '',
		vehicle_insurance_type: policy.value?.meta.vehicle_insurance_type ?? '',
		vehicle_make: policy.value?.meta.vehicle_make as DetailsType,
		first_name: policy.value?.meta.first_name ?? '',
		last_name: policy.value?.meta.last_name ?? '',
		email: policy.value?.meta.email ?? '',
		phone_number: policy.value?.meta.phone_number ?? '',
		address: policy.value?.meta.address ?? ''
	}))
	const otherMetaInfo = computed(() => policy.value?.meta || null)
	const hmoId = computed(() => (planIsGreaterThan24Hours.value ? (policy.value?.policy_number ?? '') : ''))
	const hmoCard = computed(() => (planIsGreaterThan24Hours.value ? (policy.value?.policy_certificate_url ?? '') : ''))
	const canAccessHmo = computed(() => planIsGreaterThan24Hours.value)
	const policyId = computed(() => policy.value?.id ?? '')
	const policyAuthor = computed(() => ({
		full_name: `${policy.value?.first_name ?? ''} ${policy.value?.last_name ?? ''}`,
		first_name: policy.value?.first_name ?? otherMetaInfo.value?.first_name ?? '',
		last_name: policy.value?.last_name ?? otherMetaInfo.value?.last_name ?? '',
		email: policy.value?.email ?? otherMetaInfo.value?.email ?? '',
		phone_number: policy.value?.phone_number ?? otherMetaInfo.value?.phone_number ?? '',
		relationship: policy.value?.meta?.relationship ?? '',
		title: policy.value?.owner_title ?? '',
		gender: policy.value?.gender ?? '',
		dob: policy.value?.date_of_birth ?? '',
		hmo_url: hmoCard.value,
		img_url: policy.value?.meta?.img_url ?? '',
		occupation: policy.value?.occupation ?? ''
	}))

	const gadgetMeta = computed(() => {
		return {
			type: toLower(
				(policy.value?.meta?.gadget_type as IdAndName)?.name ?? policy.value?.meta?.gadget_type ?? ''
			),
			brand: (policy.value?.meta?.gadget_brand as IdAndName)?.name ?? policy.value?.meta?.gadget_brand ?? '',
			value: policy.value?.meta?.gadget_value ?? 0,
			model: policy.value?.meta?.gadget_model ?? '',
			imei: policy.value?.meta?.imei_one ?? '',
			serial_number: policy.value?.meta?.serial_number ?? '',
			policy_number: policy.value?.policy_number ?? '',
			colour: toLower(policy.value?.meta?.gadget_color ?? ''),
			rate: policy.value?.meta?.gadget_rate ?? ''
		}
	})

	const downloadDocument = async (link: string, name = 'document') => {
		try {
			downloadingDocs.value = true
			const fileLink = link.split('.')
			const fileExtension = fileLink[fileLink.length - 1]
			await downloadFile(link, `${name}.${fileExtension}`)
		} finally {
			downloadingDocs.value = false
		}
	}
	const activatePolicy = async () => {
		try {
			resolve(() =>
				useMixpanel().track('activate_plan_biz_started', {
					plan_name: productName.value,
					plan_status: planStatus.value
				})
			)
			verifyingPolicy.value = true
			await openStaffRecord({
				purchase_id: policy.value?.purchase_id as string,
				customer_id: policy.value?.customer_id as string,
				items: policy.value || undefined
			})
		} catch {
			resolve(() =>
				useMixpanel().track('activate_plan_biz_failed', {
					plan_name: productName.value,
					plan_status: planStatus.value
				})
			)
		} finally {
			verifyingPolicy.value = false
		}
	}
	const triggerSdk = () => {
		if (policy.value?.meta?.sdk_config) {
			initializeSdk(policy.value.meta.sdk_config)
		}
	}
	const platformCompatible = () => {
		const { deviceType } = useMobile()
		if (deviceType.value === 'desktop') {
			dialogs.value.isInspectionNotSupported = true
			return Promise.resolve(false)
		} else {
			triggerSdk()
		}
		return Promise.resolve(true)
	}
	const constructInspectionLink = async () => {
		const inspectionDataResult = shallowRef<VoidInspectionContext>({
			title: categoryType.value?.isGadget ? 'Gadget Inspection' : 'Vehicle Inspection',
			key: 'inspection',
			QrCodeLink: ''
		})

		const inspectionLink = policy.value?.meta?.inspectionLink as string

		if (!inspectionLink) {
			if (!policy.value?.id) {
				return useNotification().alert({
					status: 400,
					message: 'Invalid inspection link, contact support'
				})
			}
			const { generateClaimInspectionLinks } = useResourcesApi()
			const linkResult = await resolve(async () => {
				const response = await generateClaimInspectionLinks(policy.value?.id ?? '')
				return response?.data ?? null
			})
			if (linkResult && Object.values(linkResult).length === 0) {
				return useNotification().alert({
					status: 400,
					message: 'Inspection link not available, contact support'
				})
			}
		}
		if (inspectionLink) {
			const link = new URL(inspectionLink)
			const constructed_url = `${baseUrl.value}${link?.search?.replace('?q=', '?inspectionSdk=')}`
			inspectionData.value = {
				...inspectionDataResult.value,
				QrCodeLink: constructed_url
			}
			platformCompatible()
		}
	}
	const openClaimsDialog = () => (dialogs.value.confirmClaim = true)

	const makeClaims = () => {
		/*
        Forms
        Internal Forms link
        navigateTo(`/claims/make-claim/${custodian_id}/form`)
        */
		// dialogs.confirmClaim = true
		if (policy.value?.meta?.sdk_config) {
			const configMeta = policy.value.meta.sdk_config
			const formData = {
				...configMeta,
				form: {
					policy_number: policy.value.meta.sdk_config?.policy_number || '',
					email: policy.value.email
				}
			}
			if (configMeta.pk) {
				initializeSdk({
					...formData,
					callback: () => {
						refresh?.()
						dialogs.value.confirmClaim = false
					},
					onClose: () => {
						useNotification().alert({ status: 300, message: 'Claim Sdk closed' })
						refresh?.()
						dialogs.value.confirmClaim = false
					}
				})
			}
			return null
		}
		useNotification().alert({
			status: 400,
			message: 'claim not available'
		})
	}

	const next = (step: typeof currentPage.value, idUpdate?: string) => {
		const query = {
			page: step,
			active_id: idUpdate || null
		}
		if (step === 'activation-done') {
			resolve(() =>
				useMixpanel().track('activate_plan_biz_successful', {
					plan_name: productName.value
				})
			)
		}
		if (idUpdate && policy.value?.purchase_id) {
			replace({
				path: `/manage-custodian/${policy.value.purchase_id}/activate`,
				query: {
					...query,
					active_id: idUpdate,
					page: 'submitted'
				}
			})
			return null
		}
		navigateTo({ query })
	}
	const sendReminder = () => {
		useNotification().alert({
			message: 'Reminder has been sent',
			status: 200
		})
	}
	const getDependantNewSlotInfo = (arg: { summary: object; record: ComputeAddDependantDto }) => {
		summaryContent.value = arg.summary
		formRecord.value = arg.record
		// toMakePayment.value = true
		navigateTo({
			query: {
				...urlQuery.value,
				page: 'make-payment'
			}
		})
	}
	const makePayment = async (payload: PaymentResponse) => {
		try {
			loaderProps.active = true
			loaderProps.text = 'Creating new slot'
			const result = await addNewDependantSlot({
				...formRecord.value,
				payment_channel: payload.channel,
				sales_type: 'top up',
				transaction_id: payload.transaction,
				policy_id: custodianId.value
			})
			if (result?.data) {
				resolve(() =>
					mixpanel?.track('add_dpndts.slot_biz', {
						no_of_dpndts: formRecord.value.number_of_dependant,
						plan_name: policy.value?.product?.name ?? ''
					})
				)
				useNotification().alert({
					message: result.responseText,
					status: result.responseCode
				})
				replace({
					query: {
						tab: 'dependant'
					}
				})
				await refresh?.()
			}
		} finally {
			loaderProps.active = false
		}
	}

	// Run when policy is available.
	watch(
		_payload,
		async e => {
			// Execute to check if policy has an existing claims
			if (e !== undefined && !e.pending && isPolicyClaimable.value && e.checkForClaim) {
				const response = await checkPolicyForClaim(custodianId.value)
				canMakeClaim.value = Boolean(!response?.data?.id)
			}
		},
		{ deep: true, immediate: true }
	)
	return {
		activationFailed,
		isExpired,
		isAssigned,
		healthPolicyType,
		canAccessHmo,
		isDeactivated,
		isToBeRemoved,
		hasInspected,
		pendingDependantSlots,
		hmoCard,
		hmoId,
		policyId,
		otherMetaInfo,
		policyAuthor,
		dateAdded,
		hasPendingSlots,
		canBuySlot,
		planIsGreaterThanADay: planIsGreaterThan24Hours,
		planIsGreaterThan5Days,
		isStaging,
		dependantType,
		policyCertificate,
		purchaseDate,
		expiryDate,
		provider,
		metaInfo,
		policyCustomerDetails,
		coverPeriod,
		isFamilyPlan,
		totalNoOfDependant,
		vehicleMeta,
		gadgetMeta,
		isInspectable,
		activationDate,
		renewalDate,
		verifyingPolicy,
		policy,
		isPolicyChurned,
		toMakePayment,
		canMakeClaim,
		isPolicyClaimable,
		downloadingDocs,
		terminateDialog,
		summaryContent,
		formRecord,
		loaderProps,
		back,
		addProductsIconFill,
		notSupported,
		activateModal,
		dialogs,
		inspectionData,
		categoryType,
		fromEmployeesRoute,
		currentPage,
		planStatus,
		canBeDeactivated,
		activePolicy,
		isPolicyPending,
		isPolicyPaused,
		staffName,
		policyNumber,
		profileBio,
		planName,
		categoryName,
		pageTitle,
		productTypeName,
		tabCollection,
		refresh,
		downloadDocument,
		activatePolicy,
		triggerSdk,
		platformCompatible,
		constructInspectionLink,
		makeClaims,
		next,
		sendReminder,
		getDependantNewSlotInfo,
		makePayment,
		openClaimsDialog,
		activeTab,
		loadingPolicy: computed(() => _payload.value.pending)
	}
}
